import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Box, Button, TextField, Typography, Avatar } from '@mui/material';
import { Add, RemoveCircle, KeyboardArrowLeft, KeyboardArrowRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';
import { AdminSidebarProps } from './type';
import { COLORS_ADMIN_PAGE, SIZES_PAGE } from '../conf/constants';
import { ADMIN_SIDEBAR_CONSTANTS } from './constants/adminSidebar';

// Extended interface to include onUsersPerPageChange prop
interface ExtendedAdminSidebarProps extends AdminSidebarProps {
  onUsersPerPageChange?: (usersPerPage: number) => void;
}

const AdminSidebar = forwardRef<HTMLDivElement, ExtendedAdminSidebarProps>(({
  admins,
  selectedAdmin,
  setSelectedAdmin,
  searchQuery,
  setSearchQuery,
  error,
  currentPage,
  setCurrentPage,
  usersPerPage,
  fastPageJump,
  totalAdmins,
  handleCreate,
  handleDelete,
  onUsersPerPageChange,
}, ref) => {
  const adminListRef = useRef<HTMLDivElement>(null);
  const [calculatedUsersPerPage, setCalculatedUsersPerPage] = useState(usersPerPage);

  // Function to calculate the number of items that can be displayed
  const calculateUsersPerPage = () => {
    if (!adminListRef.current) return usersPerPage;

    const containerHeight = adminListRef.current.clientHeight;
    const availableHeight = containerHeight - ADMIN_SIDEBAR_CONSTANTS.PADDING_BUFFER;
    const calculatedItems = Math.floor(availableHeight / ADMIN_SIDEBAR_CONSTANTS.ITEM_HEIGHT);
    
    // Ensure at least MIN_ITEMS are displayed
    return Math.max(ADMIN_SIDEBAR_CONSTANTS.MIN_ITEMS, calculatedItems);
  };

  // Hook to monitor size changes
  useEffect(() => {
    const updateUsersPerPage = () => {
      const newUsersPerPage = calculateUsersPerPage();
      if (newUsersPerPage !== calculatedUsersPerPage && onUsersPerPageChange) {
        setCalculatedUsersPerPage(newUsersPerPage);
        onUsersPerPageChange(newUsersPerPage);
      }
    };

    // Add small delay to ensure DOM has rendered
    const timeoutId = setTimeout(updateUsersPerPage, ADMIN_SIDEBAR_CONSTANTS.INITIAL_CALCULATION_DELAY);

    // Monitor window resize events
    const handleResize = () => {
      clearTimeout(timeoutId);
      setTimeout(updateUsersPerPage, ADMIN_SIDEBAR_CONSTANTS.RESIZE_DEBOUNCE_DELAY);
    };

    window.addEventListener('resize', handleResize);
    
    // Monitor container size changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (adminListRef.current) {
      resizeObserver.observe(adminListRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [calculatedUsersPerPage, onUsersPerPageChange]);

  const totalPages = Math.ceil(totalAdmins / usersPerPage);
  const currentAdmins = admins;

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleFastPageChange = (jump: number) => {
    const newPage = currentPage + jump * fastPageJump;
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  return (
    <Box  
      ref={ref}
      sx={{
        width: ADMIN_SIDEBAR_CONSTANTS.SIDEBAR_WIDTH,
        bgcolor: COLORS_ADMIN_PAGE.CARD_BACKGROUND,
        p: 2,
        borderRadius: 2,
        mr: 2,
        boxShadow: 1,
        minWidth: ADMIN_SIDEBAR_CONSTANTS.MIN_WIDTH,
      }}
    >
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Typography variant="h6" gutterBottom>
        {ADMIN_SIDEBAR_CONSTANTS.TITLE}
      </Typography>
      <TextField
        label={ADMIN_SIDEBAR_CONSTANTS.SEARCH_PLACEHOLDER}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box 
        ref={adminListRef}
        sx={{ 
          height: ADMIN_SIDEBAR_CONSTANTS.LIST_HEIGHT, 
          overflowY: 'auto', 
          mb: 2, 
          bgcolor: COLORS_ADMIN_PAGE.WHITE, 
          p: '8px',
          minHeight: 0 // Important: allows flex item to shrink
        }}
      >
        {currentAdmins.map((admin) => (
          <Box
            key={admin.administratorId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1,
              bgcolor: selectedAdmin?.administratorId === admin.administratorId 
                ? COLORS_ADMIN_PAGE.HOVER 
                : COLORS_ADMIN_PAGE.LIGHT_GRAY,
              color: selectedAdmin?.administratorId === admin.administratorId 
                ? COLORS_ADMIN_PAGE.WHITE 
                : 'inherit',
              mb: 1,
              height: `${ADMIN_SIDEBAR_CONSTANTS.ITEM_HEIGHT}px`, // Fixed height based on constant
              boxSizing: 'border-box',
              borderRadius: 1,
              '&:hover': {
                bgcolor: COLORS_ADMIN_PAGE.HOVER,
                color: COLORS_ADMIN_PAGE.WHITE,
              },
              cursor: 'pointer',
            }}
            onClick={() => setSelectedAdmin(admin)}
          >
            <Avatar
              src={admin.profileImageUrl || '/default-avatar.png'}
              sx={{ 
                mr: ADMIN_SIDEBAR_CONSTANTS.AVATAR.MARGIN_RIGHT,
                width: ADMIN_SIDEBAR_CONSTANTS.AVATAR.WIDTH,
                height: ADMIN_SIDEBAR_CONSTANTS.AVATAR.HEIGHT
              }}
            />
            <Typography 
              sx={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap' // Prevent text wrapping
              }}
            >
              {`${admin.firstName} ${admin.lastName}`}
            </Typography>
          </Box>
        ))}
      </Box>
      
      {/* Pagination */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mt: 1, 
        bgcolor: COLORS_ADMIN_PAGE.CARD_BACKGROUND, 
        p: 1, 
        borderRadius: 8, 
        minWidth: '300px' 
      }}>
        <Box>
          <Button
            variant="contained"
            onClick={() => handleFastPageChange(-1)}
            disabled={currentPage <= fastPageJump}
            sx={{ 
              mr: 1, 
              minWidth: ADMIN_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              paddingX: ADMIN_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
            }}
          >
            <KeyboardDoubleArrowLeft />
          </Button>
          <Button
            variant="contained"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            sx={{ 
              mr: 1, 
              minWidth: ADMIN_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              paddingX: ADMIN_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
            }}
          >
            <KeyboardArrowLeft />
          </Button>
        </Box>
        <Typography sx={{ mx: 2 }}>
          {currentPage} of {totalPages}
        </Typography>
        <Box>
          <Button
            variant="contained"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            sx={{ 
              ml: 1, 
              minWidth: ADMIN_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              paddingX: ADMIN_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
            }}
          >
            <KeyboardArrowRight />
          </Button>
          <Button
            variant="contained"
            onClick={() => handleFastPageChange(1)}
            disabled={currentPage >= totalPages - fastPageJump + 1}
            sx={{ 
              ml: 1, 
              minWidth: ADMIN_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              paddingX: ADMIN_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
            }}
          >
            <KeyboardDoubleArrowRight />
          </Button>
        </Box>
      </Box>

      {/* Action buttons */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
          sx={{ mb: 1, width: '100%', justifyContent: 'flex-start' }}
        >
          {ADMIN_SIDEBAR_CONSTANTS.CREATE_ADMIN_BUTTON}
        </Button>
        <Button
          variant="contained"
          startIcon={<RemoveCircle />}
          onClick={() => selectedAdmin && handleDelete(selectedAdmin.administratorId)}
          disabled={!selectedAdmin}
          sx={{ width: '100%', justifyContent: 'flex-start' }}
        >
          {ADMIN_SIDEBAR_CONSTANTS.DELETE_ADMIN_BUTTON}
        </Button>
      </Box>
    </Box>
  );
});

AdminSidebar.displayName = 'AdminSidebar';

export default AdminSidebar;